import {useEffect, useMemo, useState} from 'react'
import {getAuth} from '../../auth/core/AuthHelpers'

const API_URL = process.env.REACT_APP_API_URL || '/api'
const COMPANY_GROUP_ADMINS_URL = `${API_URL.replace(/\/+$/, '')}/company-group-admins`
const brandingCache = new Map<number, {logoUrl: string | null; watermarkUrl: string | null}>()

const normalizeBase = (value: string) => value.replace(/\/+$/, '')
const normalizePath = (value: string) => value.replace(/\\/g, '/')

const encodePathSegments = (rawPath: string) => {
  const leadingSlash = rawPath.startsWith('/')
  const trailingSlash = rawPath.endsWith('/') && rawPath.length > 1
  const segments = rawPath
    .split('/')
    .filter((segment, index) => !(index === 0 && segment === ''))
    .map((segment) => {
      if (!segment) return ''
      try {
        return encodeURIComponent(decodeURIComponent(segment))
      } catch {
        return encodeURIComponent(segment)
      }
    })
  const encoded = segments.join('/')
  if (!encoded) return leadingSlash ? '/' : ''
  return `${leadingSlash ? '/' : ''}${encoded}${trailingSlash ? '/' : ''}`
}

const encodePathInUrl = (value: string) => {
  try {
    const parsed = new URL(value)
    parsed.pathname = encodePathSegments(parsed.pathname || '/')
    return parsed.toString()
  } catch {
    const [withoutHash, hash = ''] = value.split('#')
    const [pathOnly, query = ''] = withoutHash.split('?')
    const encodedPath = encodePathSegments(pathOnly)
    const queryPart = query ? `?${query}` : ''
    const hashPart = hash ? `#${hash}` : ''
    return `${encodedPath}${queryPart}${hashPart}`
  }
}

const toDataUrl = async (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ''))
    reader.onerror = () => reject(reader.error || new Error('Failed to read blob'))
    reader.readAsDataURL(blob)
  })

export const resolveProcurementBrandingUrl = (rawValue: any): string | null => {
  if (rawValue == null) return null
  const value = normalizePath(String(rawValue).trim())
  if (!value || /^javascript:/i.test(value)) return null
  if (value.startsWith('data:')) return value
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(value)) return encodePathInUrl(value)

  const apiBase = normalizeBase(API_URL)
  const hostBase = apiBase.endsWith('/api') ? apiBase.slice(0, -4) : apiBase
  const stripLeading = (v: string) => v.replace(/^\/+/, '')

  if (value.startsWith('/uploads/')) return encodePathInUrl(`${hostBase}${value}`)
  if (value.startsWith('uploads/')) return encodePathInUrl(`${hostBase}/${stripLeading(value)}`)
  if (value.startsWith('/files/')) return encodePathInUrl(`${hostBase}${value}`)
  if (value.startsWith('files/')) return encodePathInUrl(`${hostBase}/${stripLeading(value)}`)
  if (value.startsWith('/api/')) return encodePathInUrl(`${hostBase}${value}`)
  if (value.startsWith('/')) return encodePathInUrl(`${apiBase}${value}`)
  return encodePathInUrl(`${apiBase}/${stripLeading(value)}`)
}

const fetchAssetDataUrl = async (rawUrl: string): Promise<string> => {
  const token = getAuth()?.auth?.jwt
  const attempts: RequestInit[] = token
    ? [
        {headers: {Authorization: `Bearer ${token}`}, credentials: 'include'},
        {headers: {Authorization: `Bearer ${token}`}},
        {credentials: 'include'},
        {},
      ]
    : [{credentials: 'include'}, {}]

  let lastError: unknown = null
  for (const options of attempts) {
    try {
      const response = await fetch(rawUrl, options)
      if (!response.ok) {
        lastError = new Error(`Asset fetch failed: ${response.status}`)
        continue
      }
      const contentType = String(response.headers.get('content-type') || '').toLowerCase()
      if (contentType && !contentType.startsWith('image/') && !contentType.includes('octet-stream')) {
        lastError = new Error(`Invalid asset content type: ${contentType}`)
        continue
      }
      const blob = await response.blob()
      return await toDataUrl(blob)
    } catch (error) {
      lastError = error
    }
  }

  throw lastError || new Error('Asset fetch failed')
}

const parseCompanyId = (rawValue: any): number | null => {
  if (rawValue == null) return null
  if (typeof rawValue === 'object') {
    return (
      parseCompanyId(rawValue.id) ??
      parseCompanyId(rawValue.cgaid) ??
      parseCompanyId(rawValue.companyGroupAdminId) ??
      parseCompanyId(rawValue.companyId)
    )
  }
  const parsed = Number(rawValue)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

const extractBrandingPayload = (payload: any): {logoUrl: string | null; watermarkUrl: string | null} => {
  if (!payload || typeof payload !== 'object') {
    return {logoUrl: null, watermarkUrl: null}
  }
  const logoUrl =
    resolveProcurementBrandingUrl(payload.logoUrl) ||
    resolveProcurementBrandingUrl(payload.companyLogoUrl) ||
    resolveProcurementBrandingUrl(payload.brandingLogoUrl)
  const watermarkUrl =
    resolveProcurementBrandingUrl(payload.watermarkUrl) ||
    resolveProcurementBrandingUrl(payload.companyWatermarkUrl) ||
    resolveProcurementBrandingUrl(payload.brandingWatermarkUrl)
  return {logoUrl, watermarkUrl}
}

const fetchCompanyBrandingFallback = async (
  companyId: number
): Promise<{logoUrl: string | null; watermarkUrl: string | null} | null> => {
  if (brandingCache.has(companyId)) {
    return brandingCache.get(companyId) || null
  }

  const token = getAuth()?.auth?.jwt
  const attempts: RequestInit[] = token
    ? [
        {headers: {Authorization: `Bearer ${token}`}, credentials: 'include'},
        {headers: {Authorization: `Bearer ${token}`}},
        {credentials: 'include'},
        {},
      ]
    : [{credentials: 'include'}, {}]

  const parseJsonSafe = async (response: Response) => {
    try {
      return await response.json()
    } catch {
      return null
    }
  }

  for (const options of attempts) {
    try {
      const direct = await fetch(`${COMPANY_GROUP_ADMINS_URL}/${companyId}`, options)
      if (direct.ok) {
        const payload = await parseJsonSafe(direct)
        const branding = extractBrandingPayload(Array.isArray(payload) ? payload[0] : payload)
        brandingCache.set(companyId, branding)
        return branding
      }
    } catch {
      // continue to list fallback
    }

    try {
      const listResponse = await fetch(COMPANY_GROUP_ADMINS_URL, options)
      if (!listResponse.ok) continue
      const payload = await parseJsonSafe(listResponse)
      const list = Array.isArray(payload) ? payload : []
      const company = list.find((item: any) => Number(item?.id) === companyId)
      const branding = extractBrandingPayload(company)
      brandingCache.set(companyId, branding)
      return branding
    } catch {
      // continue attempts
    }
  }

  return null
}

export const useProcurementBrandingAssets = (
  logoRaw: any,
  watermarkRaw: any,
  companyIdRaw?: any
) => {
  const logoUrl = useMemo(() => resolveProcurementBrandingUrl(logoRaw), [logoRaw])
  const watermarkUrl = useMemo(() => resolveProcurementBrandingUrl(watermarkRaw), [watermarkRaw])
  const companyId = useMemo(() => parseCompanyId(companyIdRaw), [companyIdRaw])
  const [logoSrc, setLogoSrc] = useState<string | null>(logoUrl)
  const [watermarkSrc, setWatermarkSrc] = useState<string | null>(watermarkUrl)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let cancelled = false
    setReady(false)
    const hydrate = async (url: string | null, setter: (value: string | null) => void) => {
      if (!url) {
        setter(null)
        return
      }
      if (url.startsWith('data:')) {
        setter(url)
        return
      }
      try {
        const dataUrl = await fetchAssetDataUrl(url)
        if (!cancelled) setter(dataUrl)
      } catch (error) {
        if (!cancelled) setter(url)
      }
    }

    const run = async () => {
      let effectiveLogoUrl = logoUrl
      let effectiveWatermarkUrl = watermarkUrl

      if ((!effectiveLogoUrl || !effectiveWatermarkUrl) && companyId) {
        const fallback = await fetchCompanyBrandingFallback(companyId).catch(() => null)
        if (fallback) {
          if (!effectiveLogoUrl) effectiveLogoUrl = fallback.logoUrl
          if (!effectiveWatermarkUrl) effectiveWatermarkUrl = fallback.watermarkUrl
        }
      }

      if (!cancelled) {
        setLogoSrc(effectiveLogoUrl)
        setWatermarkSrc(effectiveWatermarkUrl)
      }

      await Promise.all([
        hydrate(effectiveLogoUrl, setLogoSrc),
        hydrate(effectiveWatermarkUrl, setWatermarkSrc),
      ])
    }

    run().finally(() => {
      if (!cancelled) setReady(true)
    })

    return () => {
      cancelled = true
    }
  }, [logoUrl, watermarkUrl, companyId])

  return {logoSrc, watermarkSrc, ready}
}
